const INK = "#151B2F";
const INK_MUTED = "#5B6478";
const NAVY = "#232C48";
const SURFACE = "#FFFFFF";
const SURFACE_ALT = "#F6F8FA";
const BORDER = "#E3E7EE";
const LIVE_INK = "#8A5A00";
const LIVE_BG = "#FFF4D6";
const DONE_INK = "#1E6B37";
const DONE_BG = "#E6F4EA";
const GOLD = "#E8C635";

// Palette du feu de la ligne de tête. Volontairement disjointe du doré, qui
// signale « partie en cours » : deux états distincts ne peuvent pas partager
// leur couleur.
const FIRE_ORANGE = "#FF7A00";
const EMBER = "#E6390B";
const FIRE_RED_SOFT = "rgba(230, 57, 11, 0.55)";

// Palette de l'affichage joueurs : fond sombre, lu de loin sur un écran ou un
// vidéoprojecteur. Les tuiles claires du mode animateur ne tiennent pas à
// distance, d'où une déclinaison complète plutôt qu'une simple inversion.
const STAGE_BG = "#151B2F";
const STAGE_CARD = "rgba(255, 255, 255, 0.06)";
const STAGE_BORDER = "rgba(255, 255, 255, 0.12)";
const STAGE_INK = "#FFFFFF";
const STAGE_INK_MUTED = "rgba(255, 255, 255, 0.62)";

const styles = (theme) => ({
  // Cette page n'affiche jamais le menu latéral : elle est destinée à être
  // projetée. Pas de `marginLeft` réservé à la navigation, donc.
  page: {
    minHeight: "100vh",
    backgroundColor: SURFACE_ALT,
    padding: theme.spacing(3),
    boxSizing: "border-box",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1.5),
    },
  },

  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexWrap: "wrap",
    rowGap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },

  backLink: {
    textDecoration: "none",
    color: INK_MUTED,
    fontSize: "0.875rem",
    display: "inline-flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
    "&:hover": { color: NAVY },
    "&:focus-visible": {
      outline: `2px solid ${NAVY}`,
      outlineOffset: 2,
      borderRadius: 4,
    },
  },

  backIcon: {
    fontSize: 18,
    marginRight: theme.spacing(0.5),
  },

  title: {
    fontWeight: 700,
    color: NAVY,
    fontSize: "2rem",
    [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
  },

  subtitle: {
    color: INK_MUTED,
    fontSize: "0.9375rem",
    marginTop: theme.spacing(0.5),
  },

  tourTitle: {
    fontWeight: 600,
    color: LIVE_INK,
    fontSize: "1.125rem",
    marginTop: theme.spacing(0.5),
  },

  // ---------------------------------------------------------------------------
  // Barre de contrôle animateur (jamais visible en plein écran)
  // ---------------------------------------------------------------------------
  controlBar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    justifyContent: "space-between",
    backgroundColor: SURFACE,
    border: `1px solid ${BORDER}`,
    borderRadius: 12,
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2),
    rowGap: theme.spacing(1),
  },

  controlGroup: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    rowGap: theme.spacing(1),
  },

  runInfo: {
    color: INK_MUTED,
    fontSize: "0.8125rem",
    marginRight: theme.spacing(2),
  },

  runInfoStrong: {
    color: INK,
    fontWeight: 600,
  },

  primaryButton: {
    textTransform: "none",
    fontWeight: 600,
    backgroundColor: NAVY,
    color: SURFACE,
    marginLeft: theme.spacing(1),
    "&:hover": { backgroundColor: "#1a2136" },
  },

  secondaryButton: {
    textTransform: "none",
    fontWeight: 600,
    marginLeft: theme.spacing(1),
    color: NAVY,
    borderColor: BORDER,
    "&:hover": {
      borderColor: NAVY,
      backgroundColor: "rgba(35, 44, 72, 0.04)",
    },
  },

  // Le zoom est appliqué ici et non sur `page` : la racine conserve son fond et
  // sa hauteur de viewport, cette couche est la seule à changer d'échelle.
  zoomLayer: {
    width: "100%",
  },

  // ---------------------------------------------------------------------------
  // Réglages d'affichage (zoom, défilement auto)
  // ---------------------------------------------------------------------------
  displayControls: {
    display: "flex",
    alignItems: "center",
    marginRight: theme.spacing(2),
  },

  zoomGroup: {
    display: "flex",
    alignItems: "center",
    border: `1px solid ${BORDER}`,
    borderRadius: 20,
    padding: theme.spacing(0, 0.5),
    marginRight: theme.spacing(1),
  },

  zoomButton: {
    color: INK_MUTED,
    padding: 6,
    "&:hover": { color: NAVY },
    "&.Mui-disabled": { color: "#C3C8D2" },
  },

  // Largeur fixe et chiffres tabulaires : le libellé passe de « 80% » à « 100% »
  // sans décaler les boutons de part et d'autre.
  zoomValue: {
    color: INK,
    fontSize: "0.75rem",
    fontWeight: 600,
    width: 40,
    textAlign: "center",
    fontVariantNumeric: "tabular-nums",
  },

  autoScrollButton: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.75rem",
    color: INK_MUTED,
    borderColor: BORDER,
    borderRadius: 20,
    padding: theme.spacing(0.25, 1.5),
  },

  autoScrollButtonActive: {
    color: NAVY,
    borderColor: NAVY,
    backgroundColor: "rgba(35, 44, 72, 0.06)",
  },

  liveDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: GOLD,
    marginRight: theme.spacing(1),
    flexShrink: 0,
    animation: "$pulse 1.6s ease-in-out infinite",
  },

  "@keyframes pulse": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.25 },
  },

  // ---------------------------------------------------------------------------
  // Bandeau du mode joueurs
  // ---------------------------------------------------------------------------
  stageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    rowGap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },

  stageBrand: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
  },

  stageLogo: {
    width: 48,
    height: 48,
    borderRadius: 8,
    marginRight: theme.spacing(2),
    flexShrink: 0,
  },

  stageTourTitle: {
    fontWeight: 700,
    color: STAGE_INK,
    fontSize: "1.75rem",
    lineHeight: 1.2,
    wordBreak: "break-word",
  },

  stageSubtitle: {
    color: STAGE_INK_MUTED,
    fontSize: "1rem",
  },

  stageStatus: {
    display: "flex",
    alignItems: "center",
    color: STAGE_INK_MUTED,
    fontSize: "1rem",
    whiteSpace: "nowrap",
    flexWrap: "wrap",
    rowGap: theme.spacing(1),
  },

  exitButton: {
    color: STAGE_INK_MUTED,
    marginLeft: theme.spacing(2),
    "&:hover": { color: STAGE_INK },
    "&.Mui-focusVisible": { outline: "2px solid rgba(255, 255, 255, 0.8)" },
  },

  // ---------------------------------------------------------------------------
  // Cartes de session
  // ---------------------------------------------------------------------------
  // Ligne de session. La hauteur est volontairement contrainte : c'est elle qui
  // détermine combien d'équipes tiennent à l'écran.
  card: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    backgroundColor: SURFACE,
    border: `1px solid ${BORDER}`,
    borderRadius: 10,
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(1),
  },

  cardLive: {
    borderColor: GOLD,
    backgroundColor: "#FFFCF2",
  },

  // ---------------------------------------------------------------------------
  // Ligne de tête : feu
  //
  // Tout est en CSS, ni image ni librairie, pour rester net à n'importe quel
  // zoom et sur un vidéoprojecteur.
  //
  // La teinte est franchement rouge/orangée et jamais dorée : le doré est déjà
  // pris par « partie en cours » (`cardLive`), et les deux états se
  // confondaient. Ici le feu et la bordure sont chauds côté rouge, la partie en
  // cours reste jaune.
  // ---------------------------------------------------------------------------
  cardLeader: {
    borderColor: EMBER,
    backgroundColor: "#FFF5F0",
    // Lueur interne diffuse au ras du bas : elle ancre le feu sans dessiner la
    // ligne horizontale nette qui se lisait comme une barre de progression.
    boxShadow: `inset 0 -10px 16px -8px ${FIRE_RED_SOFT}`,
    // Le contenu de la ligne doit rester au-dessus du feu.
    "& > *:not(:first-child)": {
      position: "relative",
      zIndex: 1,
    },
  },

  // Dégradé orangé remontant du bord bas, sans contour net : c'est une lueur,
  // pas une barre. L'échelle part de la base (`transformOrigin`) pour qu'en
  // enflant elle monte dans la ligne au lieu de déborder sous la carte.
  leaderGlow: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 14,
    pointerEvents: "none",
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    background: `linear-gradient(to top, ${FIRE_ORANGE} 0%, rgba(255, 122, 0, 0.45) 45%, rgba(255, 122, 0, 0) 100%)`,
    transformOrigin: "50% 100%",
    animation: "$emberBreath 3.6s ease-in-out infinite",
  },

  // Respiration lente : la lueur enfle, puis redescend, l'opacité suivant le
  // même mouvement. Volontairement long et à faible amplitude — la page reste
  // affichée devant un groupe pendant toute une partie, une pulsation rapide
  // deviendrait fatigante.
  "@keyframes emberBreath": {
    "0%, 100%": { transform: "scaleY(0.85)", opacity: 0.26 },
    "50%": { transform: "scaleY(1.12)", opacity: 0.46 },
  },

  // Une animation permanente en périphérie du regard est un motif classique de
  // gêne (voire de déclencheur) : on la coupe si le système le demande, en
  // gardant la lueur figée pour que la ligne de tête reste identifiable.
  "@media (prefers-reduced-motion: reduce)": {
    liveDot: {
      animation: "none",
    },
    leaderGlow: {
      animation: "none",
      // `animation: none` reviendrait sinon à une opacité de 1, plus marquée
      // que n'importe quelle étape de la version animée.
      opacity: 0.36,
    },
  },

  rank: {
    fontWeight: 700,
    color: INK_MUTED,
    fontSize: "1.25rem",
    width: 40,
    flexShrink: 0,
    fontVariantNumeric: "tabular-nums",
  },

  // Le rang du leader reprend la teinte du feu, pas le doré de « en cours ».
  rankLeader: {
    color: EMBER,
  },

  // Seule colonne élastique : elle absorbe la largeur restante et pousse le
  // score contre le bord droit.
  teamRow: {
    display: "flex",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
  },

  // `minWidth: 0` sur le parent + ellipsis ici : un nom d'équipe à rallonge
  // doit être tronqué, jamais faire passer la ligne sur deux hauteurs.
  teamName: {
    fontWeight: 600,
    color: INK,
    fontSize: "1.125rem",
    // Le 1.5 par défaut de Typography réserve de la place pour une deuxième
    // ligne qui n'arrivera jamais : c'est lui qui fixait la hauteur de la ligne.
    lineHeight: 1.2,
    marginRight: theme.spacing(1.5),
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  // Sur petit écran il n'y a pas la place : le détail cède, le nom et le score
  // restent.
  cardMeta: {
    color: INK_MUTED,
    fontSize: "0.8125rem",
    whiteSpace: "nowrap",
    flexShrink: 0,
    marginLeft: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  // Seule colonne de la ligne à tenir sur deux niveaux : le score, puis son
  // libellé centré dessous. Les interlignes sont serrés au maximum, c'est ce
  // bloc qui fixe désormais la hauteur de la ligne.
  scoreBlock: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    minWidth: 132,
    marginLeft: theme.spacing(2),
  },

  scoreValue: {
    fontWeight: 700,
    color: NAVY,
    fontSize: "1.5rem",
    lineHeight: 1,
    fontVariantNumeric: "tabular-nums",
  },

  scoreTotal: {
    color: INK_MUTED,
    fontSize: "0.875rem",
    fontWeight: 600,
  },

  scoreLabel: {
    color: INK_MUTED,
    fontSize: "0.6875rem",
    lineHeight: 1,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },

  statusChip: {
    fontWeight: 600,
    fontSize: "0.75rem",
    height: 24,
  },

  statusChipLive: {
    backgroundColor: LIVE_BG,
    color: LIVE_INK,
  },

  statusChipDone: {
    backgroundColor: DONE_BG,
    color: DONE_INK,
  },

  statusChipLabel: {
    display: "flex",
    alignItems: "center",
  },

  statusChipIcon: {
    fontSize: 14,
    marginRight: theme.spacing(0.75),
  },

  // ---------------------------------------------------------------------------
  // États vides
  // ---------------------------------------------------------------------------
  empty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    padding: theme.spacing(6, 3),
    backgroundColor: SURFACE,
    border: `1px dashed ${BORDER}`,
    borderRadius: 12,
  },

  emptyIcon: {
    fontSize: 48,
    color: "#B6BDCB",
    marginBottom: theme.spacing(1.5),
  },

  emptyTitle: {
    fontWeight: 600,
    color: INK,
    marginBottom: theme.spacing(0.5),
  },

  emptyText: {
    color: INK_MUTED,
    fontSize: "0.9375rem",
    maxWidth: 480,
    marginBottom: theme.spacing(2.5),
  },

  // ---------------------------------------------------------------------------
  // Mode joueurs (plein écran) : mêmes composants, échelle et contraste revus
  // pour une lecture à distance. Les commandes animateur ne sont pas montées.
  // ---------------------------------------------------------------------------
  pageStage: {
    backgroundColor: STAGE_BG,
    padding: theme.spacing(4),
    overflowY: "auto",

    // Lecture à distance : on monte les tailles sans revenir à des lignes
    // hautes, sinon on reperd ce que la mise en ligne unique a gagné.
    "& $card": {
      backgroundColor: STAGE_CARD,
      borderColor: STAGE_BORDER,
      borderRadius: 12,
      padding: theme.spacing(1.25, 3),
      marginBottom: theme.spacing(1.25),
    },

    "& $cardLive": {
      backgroundColor: "rgba(232, 198, 53, 0.12)",
      borderColor: "rgba(232, 198, 53, 0.5)",
    },

    "& $rank": {
      color: STAGE_INK_MUTED,
      fontSize: "1.75rem",
      width: 64,
    },

    "& $rankLeader": {
      color: FIRE_ORANGE,
    },

    "& $teamName": {
      color: STAGE_INK,
      fontSize: "1.75rem",
    },

    "& $cardMeta": {
      color: STAGE_INK_MUTED,
      fontSize: "1.125rem",
      // En plein écran la place ne manque pas : le détail reste affiché même
      // sur un écran étroit.
      [theme.breakpoints.down("sm")]: {
        display: "inline",
      },
    },

    "& $scoreBlock": {
      minWidth: 180,
    },

    "& $scoreValue": {
      color: STAGE_INK,
      fontSize: "2.25rem",
    },

    "& $scoreTotal": {
      color: STAGE_INK_MUTED,
      fontSize: "1.125rem",
    },

    "& $scoreLabel": {
      color: STAGE_INK_MUTED,
      fontSize: "0.875rem",
    },

    "& $statusChip": {
      fontSize: "0.9375rem",
      height: 32,
    },

    "& $statusChipLive": {
      backgroundColor: "rgba(232, 198, 53, 0.18)",
      color: GOLD,
    },

    "& $statusChipDone": {
      backgroundColor: "rgba(255, 255, 255, 0.12)",
      color: STAGE_INK_MUTED,
    },

    "& $statusChipIcon": {
      fontSize: 18,
    },

    "& $liveDot": {
      width: 10,
      height: 10,
    },

    // Lueur plus haute et plus dense : sur fond sombre et vue de loin,
    // l'échelle du mode animateur s'efface.
    "& $leaderGlow": {
      height: 20,
      borderBottomLeftRadius: 12,
      borderBottomRightRadius: 12,
      background: `linear-gradient(to top, ${FIRE_ORANGE} 0%, rgba(255, 122, 0, 0.5) 45%, rgba(255, 122, 0, 0) 100%)`,
    },

    "& $cardLeader": {
      borderColor: "rgba(255, 122, 0, 0.75)",
      backgroundColor: "rgba(230, 57, 11, 0.16)",
      boxShadow: `inset 0 -14px 22px -10px ${FIRE_RED_SOFT}`,
    },

    // Réglages en clair sur le fond sombre du mode joueurs.
    "& $zoomGroup": {
      borderColor: STAGE_BORDER,
    },

    "& $zoomButton": {
      color: STAGE_INK_MUTED,
      "&:hover": { color: STAGE_INK },
    },

    "& $zoomButton.Mui-disabled": {
      color: "rgba(255, 255, 255, 0.25)",
    },

    "& $zoomValue": {
      color: STAGE_INK,
    },

    "& $autoScrollButton": {
      color: STAGE_INK_MUTED,
      borderColor: STAGE_BORDER,
    },

    "& $autoScrollButtonActive": {
      color: GOLD,
      borderColor: "rgba(232, 198, 53, 0.6)",
      backgroundColor: "rgba(232, 198, 53, 0.12)",
    },

    "& $empty": {
      backgroundColor: "transparent",
      borderColor: STAGE_BORDER,
      padding: theme.spacing(10, 3),
    },

    "& $emptyTitle": {
      color: STAGE_INK,
      fontSize: "1.75rem",
    },

    "& $emptyText": {
      color: STAGE_INK_MUTED,
      fontSize: "1.125rem",
      maxWidth: 640,
    },

    "& $emptyIcon": {
      color: STAGE_INK_MUTED,
      fontSize: 64,
    },
  },
});

export default styles;
